// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a toggle component for managing a location's inventory status.
 * It presents a user-friendly interface for switching between inventory and non-inventory
 * states, with appropriate visual feedback and help text.
 */
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import {
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  styled,
  toggleButtonClasses,
  toggleButtonGroupClasses,
} from '@mui/material';

/**
 * Styled version of Material-UI's ToggleButtonGroup.
 * Customizes the appearance of toggle buttons to match Hardfin's design:
 * - Removes default borders
 * - Adds consistent spacing
 * - Uses rounded corners
 * - Maintains readable text
 */
const InventoryButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonClasses.root}.${toggleButtonGroupClasses.grouped}`]: {
    border: 0,
    margin: theme.spacing(0.5),
    padding: theme.spacing(0.5, 2),
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none',
  },
}));

interface Props {
  /**
   * Current inventory status of the location.
   * - true: Location is included in inventory
   * - false: Location is excluded from inventory
   * - null: No inventory status set (initial state)
   */
  isInventory: boolean | null;

  /**
   * Callback fired when the inventory status changes.
   * @param isInventory - The new inventory status
   */
  onChange: (isInventory: boolean) => void;
}

/**
 * A toggle component for controlling a location's inventory status.
 * Provides a simple Yes/No interface with an information tooltip explaining
 * the implications of the setting.
 *
 * Features:
 * - Clear visual feedback of current state
 * - Informative tooltip explaining the setting's purpose
 * - Handles null/unset initial states
 * - Prevents deselection of current value
 *
 * @example
 * ```tsx
 * <LocationInventoryToggle
 *   isInventory={location.isInventory}
 *   onChange={handleInventoryChange}
 * />
 * ```
 */
export function LocationInventoryToggle(props: Props) {
  // Convert boolean/null to string value for toggle group
  const value =
    props.isInventory === null
      ? null
      : props.isInventory
        ? 'inventory'
        : 'non-inventory';

  /**
   * Handles changes to the toggle button group.
   * Converts the string value back to boolean and calls onChange.
   * Prevents null selection by ignoring toggle of currently selected value.
   */
  const handleChangeInventoryToggle = (
    _: unknown,
    value: 'inventory' | 'non-inventory' | null
  ) => {
    if (value === null) {
      return;
    }

    const isInventory = value === 'inventory';
    props.onChange(isInventory);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      paddingX={1}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2">Include in inventory</Typography>
        <Tooltip
          arrow
          placement="right"
          title="Assets at this location are considered in inventory for reporting purposes"
        >
          <InfoOutlined fontSize="small" color="action" />
        </Tooltip>
      </Stack>
      <Paper variant="outlined">
        <InventoryButtonGroup
          exclusive
          fullWidth
          size="small"
          value={value}
          onChange={handleChangeInventoryToggle}
        >
          <ToggleButton value="inventory">Yes</ToggleButton>
          <ToggleButton value="non-inventory">No</ToggleButton>
        </InventoryButtonGroup>
      </Paper>
    </Stack>
  );
}
